import { captureException } from '@sentry/nuxt'

/**
 * Vue-specific DOM patching for handling hydration and reactivity edge cases
 * Focuses on Vue's comment markers and text node operations
 */
export default defineNuxtPlugin((nuxtApp) => {
  if (process.server) return

  console.log('[Vue DOM Patch] Initializing Vue-specific DOM protection...')

  const isDev = process.env.NODE_ENV !== 'production'
  const isVueComment = (node: Node): boolean => {
    return !!(
      node.nodeType === Node.COMMENT_NODE &&
      node.textContent &&
      (node.textContent.includes('[') ||
        node.textContent.includes(']') ||
        node.textContent === '')
    )
  }

  // Track errors by component/location
  const errorMap = new Map<string, number>()

  const getNodeDescription = (node: Node | null): string => {
    if (!node) return 'null'
    if (node.nodeType === Node.TEXT_NODE) {
      return `text("${node.textContent?.substring(0, 20)}...")`
    }
    if (node.nodeType === Node.COMMENT_NODE) {
      return `comment("${node.textContent}")`
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element
      return `<${el.tagName.toLowerCase()}${el.id ? `#${el.id}` : ''}${
        el.className ? `.${el.className.split(' ').join('.')}` : ''
      }>`
    }
    return `node(type:${node.nodeType})`
  }

  const getComponentContext = (): {
    name: string
    stack: string[]
    fullStack?: string
  } => {
    // Try to identify the Vue component from the stack trace
    try {
      const stack = new Error().stack || ''
      const lines = stack.split('\n')

      // Parse Vue component names from stack
      const vueComponents: string[] = []
      const stackLines: string[] = []

      lines.forEach((line) => {
        // Extract .vue file references
        const vueMatch = line.match(/(\w+)\.vue/)
        if (vueMatch && vueMatch[1]) {
          vueComponents.push(vueMatch[1])
        }
        // Clean up stack line for readability
        const cleanLine = line
          .trim()
          .replace(/^at /, '')
          .replace(/\s+\(.+\)$/, '')
        if (cleanLine) {
          stackLines.push(cleanLine)
        }
      })

      return {
        name: vueComponents[0] || 'unknown',
        stack: vueComponents,
        fullStack: isDev ? stack : undefined,
      }
    } catch {}
    return { name: 'unknown', stack: [], fullStack: undefined }
  }

  // Patch insertBefore
  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(
    newChild: T,
    referenceChild: Node | null,
  ): T {
    // DEFENSIVE: Check parent is valid before attempting operation
    if (!this || !(this instanceof Node)) {
      console.warn(
        '[Vue DOM Patch] insertBefore called with null/invalid parent',
      )
      return newChild
    }

    // ENHANCED MONITORING: Capture timing and parent state for diagnostics
    const startTime = performance.now()
    const parentBeforeOperation = this
    const parentConnectedBefore = this.isConnected

    // TOCTOU NOTE: Race condition exists between this point and native insertBefore call
    // Parent could become null/disconnected during async operations
    // We handle this via try-catch rather than additional checks (which can't prevent it)
    try {
      return originalInsertBefore.call(this, newChild, referenceChild) as T
    } catch (lastError) {
      // Handled exception - captured to Sentry for monitoring fix effectiveness
      // Component-level guards (isMountedRef) should prevent most errors
      const endTime = performance.now()
      const timingMs = endTime - startTime

      const component = getComponentContext()
      const errorKey = `${component.name}:${getNodeDescription(
        newChild,
      )}:${getNodeDescription(referenceChild)}`

      // Track error frequency
      const count = (errorMap.get(errorKey) || 0) + 1
      errorMap.set(errorKey, count)

      // ENHANCED MONITORING: Check if parent state changed during operation
      const parentConnectedAfter = (parentBeforeOperation as Node).isConnected

      // Only log first occurrence and milestones
      if (count === 1 || count === 10 || count === 100) {
        // Capture to Sentry as handled exception so we can track if fix worked
        captureException(lastError, {
          level: 'warning',
          tags: {
            component: 'vue-dom-patch',
            operation: 'insertBefore',
            occurrenceCount: count,
          },
          contexts: {
            vue: {
              componentName: component.name,
              componentStack: component.stack,
            },
            dom: {
              parent: getNodeDescription(this),
              newChild: getNodeDescription(newChild),
              referenceChild: getNodeDescription(referenceChild),
              parentConnectedBefore,
              parentConnectedAfter,
              parentStateChanged:
                parentConnectedBefore !== parentConnectedAfter,
            },
            timing: {
              durationMs: timingMs.toFixed(2),
            },
          },
        })

        console.warn(
          `[Vue DOM Patch] insertBefore failed (${count}x)`,
          {
            // Component context with full hierarchy
            componentName: component.name,
            componentStack: component.stack,
            operation: 'insertBefore',

            // Node descriptions
            parent: getNodeDescription(this),
            newChild: getNodeDescription(newChild),
            referenceChild: getNodeDescription(referenceChild),

            // Parent state tracking
            parentConnectedBefore,
            parentConnectedAfter,
            parentStateChanged: parentConnectedBefore !== parentConnectedAfter,

            // Timing information
            timingMs: timingMs.toFixed(2),

            // Check if we're in a content-visibility hidden subtree
            inHiddenSubtree: (() => {
              let el = this as any
              while (el && el.style) {
                const computed = window.getComputedStyle(el)
                if (
                  computed.contentVisibility === 'hidden' ||
                  computed.visibility === 'hidden' ||
                  computed.display === 'none'
                ) {
                  return true
                }
                el = el.parentElement
              }
              return false
            })(),

            // Full stack trace in development
            ...(isDev && component.fullStack
              ? { stackTrace: component.fullStack }
              : {}),
          },
          isDev ? lastError : undefined,
        )
      }

      // Intelligent fallback based on node types
      if (isVueComment(newChild)) {
        // Vue comment markers can be safely ignored
        return newChild
      }

      if (!this.isConnected) {
        // Parent is disconnected, operation would fail anyway
        return newChild
      }

      // Try appendChild as fallback for null reference
      if (!referenceChild) {
        try {
          return this.appendChild(newChild) as T
        } catch {
          // Silent fail
        }
      }

      return newChild as T
    }
  }

  // Patch removeChild
  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    try {
      return originalRemoveChild.call(this, child) as T
    } catch (error) {
      const component = getComponentContext()
      const errorKey = `${component}:remove:${getNodeDescription(child)}`
      const count = (errorMap.get(errorKey) || 0) + 1
      errorMap.set(errorKey, count)

      if (count === 1) {
        console.warn(
          '[Vue DOM Patch] removeChild failed',
          {
            component,
            operation: 'removeChild',
            parent: getNodeDescription(this),
            child: getNodeDescription(child),
            parentConnected: this.isConnected,
          },
          isDev ? error : undefined,
        )
      }

      return child as T
    }
  }

  // Patch replaceChild
  const originalReplaceChild = Node.prototype.replaceChild
  Node.prototype.replaceChild = function <T extends Node>(
    newChild: Node,
    oldChild: T,
  ): T {
    try {
      return originalReplaceChild.call(this, newChild, oldChild) as T
    } catch (error) {
      const component = getComponentContext()
      console.warn(
        '[Vue DOM Patch] replaceChild failed',
        {
          component,
          operation: 'replaceChild',
          parent: getNodeDescription(this),
          newChild: getNodeDescription(newChild),
          oldChild: getNodeDescription(oldChild),
        },
        isDev ? error : undefined,
      )

      // Try to remove old and append new as fallback
      try {
        this.removeChild(oldChild)
        this.appendChild(newChild)
      } catch {
        // Silent fail
      }

      return oldChild as T
    }
  }

  // Development tools
  if (isDev && process.client) {
    ;(window as any).__vueDomPatch = {
      getErrors: () => {
        const errors = Array.from(errorMap.entries())
          .map(([key, count]) => ({ error: key, count }))
          .sort((a, b) => b.count - a.count)
        console.table(errors)
        return errors
      },
      clearErrors: () => {
        errorMap.clear()
        console.log('[Vue DOM Patch] Error map cleared')
      },
      disable: () => {
        Node.prototype.insertBefore = originalInsertBefore
        Node.prototype.removeChild = originalRemoveChild
        Node.prototype.replaceChild = originalReplaceChild
        console.log('[Vue DOM Patch] Disabled')
      },
    }

    // Log summary periodically in development
    setInterval(() => {
      if (errorMap.size > 0) {
        console.debug(
          `[Vue DOM Patch] ${errorMap.size} unique errors, ${Array.from(
            errorMap.values(),
          ).reduce((a, b) => a + b, 0)} total occurrences`,
        )
      }
    }, 60000) // Every minute
  }

  // Helper to handle null reference errors
  const handleNullReferenceError = (error: any, event?: Event) => {
    if (error && error.message) {
      // Check for null reference errors on DOM methods
      if (
        error.message.includes(
          "Cannot read properties of null (reading 'insertBefore')",
        ) ||
        error.message.includes(
          "Cannot read properties of null (reading 'removeChild')",
        ) ||
        error.message.includes(
          "Cannot read properties of null (reading 'replaceChild')",
        ) ||
        error.message.includes(
          "Cannot read properties of null (reading 'appendChild')",
        )
      ) {
        if (event) event.preventDefault() // Prevent Sentry from catching it

        const component = getComponentContext()
        const errorKey = `null-reference:${error.message.match(/'(\w+)'/)?.[1] || 'unknown'}`
        const count = (errorMap.get(errorKey) || 0) + 1
        errorMap.set(errorKey, count)

        if (count === 1 || count === 10 || count === 100) {
          console.warn(
            `[Vue DOM Patch] Null reference error caught (${count}x)`,
            {
              component,
              error: error.message,
              stack: isDev ? error.stack : undefined,
            },
          )
        }
        return true
      }
    }
    return false
  }

  // Catch null reference errors from unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    handleNullReferenceError(event.reason, event)
  })

  // Also catch them from regular errors
  window.addEventListener(
    'error',
    (event) => {
      if (handleNullReferenceError(event.error, event)) {
        // Error was handled
        return
      }
    },
    true,
  )

  // Listen for route changes to clear error map
  nuxtApp.hook('app:mounted', () => {
    const router = nuxtApp.$router as
      | { afterEach?: (callback: () => void) => void }
      | undefined
    router?.afterEach?.(() => {
      // Clear error map on route change to track per-page issues
      if (errorMap.size > 10) {
        errorMap.clear()
      }
    })
  })

  // Verify patches are working
  const patchesApplied =
    Node.prototype.insertBefore !== originalInsertBefore &&
    Node.prototype.removeChild !== originalRemoveChild &&
    Node.prototype.replaceChild !== originalReplaceChild

  if (patchesApplied) {
    console.log(
      '[Vue DOM Patch] ✅ Vue DOM protection active - DOM operations protected against failures and null references',
    )
    console.log(
      '[Vue DOM Patch] 📊 Use window.__vueDomPatch.getErrors() to see any caught errors',
    )
  } else {
    console.error('[Vue DOM Patch] ❌ FAILED to apply some patches!')
  }
})
